import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { deleteNews, getAdminNews } from "./newsApi";

const colors = {
    cream: "#fffaf2",
    paper: "rgba(255, 250, 242, 0.94)",
    white: "rgba(255,255,255,0.72)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    softMuted: "rgba(42, 38, 33, 0.48)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    brownDarker: "#543b26",
    border: "rgba(155,118,83,0.16)",
    success: "#1f6f3f",
    successBg: "rgba(31,111,63,0.1)",
    draftBg: "rgba(155,118,83,0.12)",
    danger: "#9d2b2b",
    dangerBg: "rgba(157,43,43,0.08)",
};

function formatDate(dateString) {
    if (!dateString) return "—";

    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(new Date(dateString));
}

function AdminNewsList() {
    const navigate = useNavigate();

    const [news, setNews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    async function loadNews() {
        try {
            setIsLoading(true);
            setErrorMessage("");

            const data = await getAdminNews();
            setNews(data || []);
        } catch (error) {
            setErrorMessage("Beiträge konnten nicht geladen werden.");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleDelete(id) {
        const confirmed = window.confirm(
            "Möchtest du diesen Beitrag wirklich löschen?"
        );

        if (!confirmed) return;

        try {
            setErrorMessage("");
            await deleteNews(id);
            await loadNews();
        } catch (error) {
            setErrorMessage("Beitrag konnte nicht gelöscht werden.");
            console.error(error);
        }
    }

    useEffect(() => {
        loadNews();
    }, []);

    const stats = useMemo(() => {
        const publishedCount = news.filter((item) => item.published).length;
        const draftCount = news.length - publishedCount;

        return [
            {
                label: "Gesamt",
                value: news.length,
                helper: "Beiträge im System",
            },
            {
                label: "Online",
                value: publishedCount,
                helper: "Veröffentlicht",
            },
            {
                label: "Entwürfe",
                value: draftCount,
                helper: "Noch nicht sichtbar",
            },
        ];
    }, [news]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: {
                    xs: 3,
                    md: 4,
                },
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    p: {
                        xs: 3,
                        sm: 4,
                        md: 5,
                    },
                    borderRadius: {
                        xs: 4,
                        md: 6,
                    },
                    border: `1px solid ${colors.border}`,
                    background:
                        "radial-gradient(circle at 88% 14%, rgba(155,118,83,0.18) 0%, rgba(155,118,83,0) 30%), linear-gradient(145deg, rgba(255,250,242,0.96) 0%, rgba(247,241,232,0.92) 100%)",
                    boxShadow:
                        "0 24px 80px rgba(78,54,30,0.1), 0 2px 12px rgba(78,54,30,0.05)",
                }}
            >
                <Box
                    aria-hidden="true"
                    sx={{
                        position: "absolute",
                        width: 260,
                        height: 260,
                        borderRadius: "50%",
                        right: -90,
                        top: -110,
                        backgroundColor: "rgba(155,118,83,0.12)",
                        filter: "blur(8px)",
                    }}
                />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "1.25fr auto",
                        },
                        alignItems: "center",
                        gap: {
                            xs: 3,
                            md: 5,
                        },
                    }}
                >
                    <Box>
                        <Typography
                            component="p"
                            sx={{
                                mb: 1,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 13,
                                fontWeight: 900,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: colors.brown,
                            }}
                        >
                            Beiträge
                        </Typography>

                        <Typography
                            component="h1"
                            sx={{
                                mb: 1.5,
                                maxWidth: 760,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "2.35rem",
                                    sm: "3rem",
                                    md: "3.65rem",
                                },
                                lineHeight: 0.95,
                                fontWeight: 800,
                                letterSpacing: "-0.06em",
                                color: colors.ink,
                            }}
                        >
                            Aktuelles verwalten.
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 680,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 16,
                                    md: 17,
                                },
                                lineHeight: 1.75,
                                color: colors.muted,
                            }}
                        >
                            Erstelle, bearbeite und veröffentliche Neuigkeiten für die
                            Website. Entwürfe bleiben verborgen, veröffentlichte Beiträge
                            sind für Besucher sichtbar.
                        </Typography>
                    </Box>

                    <Stack
                        spacing={1.4}
                        sx={{
                            alignItems: {
                                xs: "stretch",
                                md: "flex-end",
                            },
                        }}
                    >
                        <Button
                            type="button"
                            onClick={() => navigate("/admin/news/new")}
                            variant="contained"
                            sx={{
                                minHeight: 54,
                                px: 3.3,
                                borderRadius: 999,
                                backgroundColor: colors.brownDark,
                                color: "#fff",
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.07em",
                                boxShadow: "0 14px 30px rgba(111,81,54,0.22)",

                                "&:hover": {
                                    backgroundColor: colors.brownDarker,
                                    boxShadow: "0 18px 38px rgba(111,81,54,0.28)",
                                },
                            }}
                        >
                            Neuer Beitrag
                        </Button>

                        <Button
                            type="button"
                            onClick={loadNews}
                            disabled={isLoading}
                            variant="text"
                            sx={{
                                borderRadius: 999,
                                color: colors.brownDark,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontWeight: 900,
                                textTransform: "none",

                                "&:hover": {
                                    backgroundColor: "rgba(155,118,83,0.08)",
                                },
                            }}
                        >
                            {isLoading ? "Wird geladen..." : "Liste aktualisieren"}
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {errorMessage && (
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 3,
                        backgroundColor: "rgba(211,47,47,0.08)",
                        fontFamily: '"Nunito Sans", sans-serif',
                        "& .MuiAlert-message": {
                            fontWeight: 700,
                        },
                    }}
                >
                    {errorMessage}
                </Alert>
            )}

            {!isLoading && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(3, 1fr)",
                        },
                        gap: 2,
                    }}
                >
                    {stats.map((item) => (
                        <Paper
                            key={item.label}
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: 4,
                                backgroundColor: colors.paper,
                                border: `1px solid ${colors.border}`,
                                boxShadow: "0 16px 44px rgba(78,54,30,0.06)",
                            }}
                        >
                            <Typography
                                sx={{
                                    mb: 0.8,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 900,
                                    letterSpacing: "0.14em",
                                    textTransform: "uppercase",
                                    color: colors.brown,
                                }}
                            >
                                {item.label}
                            </Typography>

                            <Typography
                                sx={{
                                    mb: 0.6,
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontSize: "1.9rem",
                                    lineHeight: 1,
                                    fontWeight: 800,
                                    letterSpacing: "-0.05em",
                                    color: colors.ink,
                                }}
                            >
                                {item.value}
                            </Typography>

                            <Typography
                                sx={{
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 14,
                                    lineHeight: 1.55,
                                    color: colors.softMuted,
                                }}
                            >
                                {item.helper}
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            )}

            {isLoading ? (
                <Paper
                    elevation={0}
                    sx={{
                        py: 8,
                        px: 3,
                        borderRadius: 5,
                        backgroundColor: colors.paper,
                        border: `1px solid ${colors.border}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <CircularProgress
                        sx={{
                            color: colors.brownDark,
                        }}
                    />

                    <Typography
                        sx={{
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 15,
                            fontWeight: 800,
                            color: colors.muted,
                        }}
                    >
                        Beiträge werden geladen...
                    </Typography>
                </Paper>
            ) : news.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        p: {
                            xs: 3,
                            md: 5,
                        },
                        borderRadius: 5,
                        backgroundColor: colors.paper,
                        border: `1px solid ${colors.border}`,
                        boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
                    }}
                >
                    <Box
                        aria-hidden="true"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 58,
                            height: 58,
                            mb: 2.5,
                            borderRadius: 3,
                            backgroundColor: "rgba(155,118,83,0.12)",
                        }}
                    >
                        <Typography sx={{ fontSize: 26, lineHeight: 1 }}>
                            ✍️
                        </Typography>
                    </Box>

                    <Typography
                        component="h2"
                        sx={{
                            mb: 1,
                            fontFamily: '"Quicksand", sans-serif',
                            fontSize: {
                                xs: "2rem",
                                md: "2.5rem",
                            },
                            lineHeight: 1,
                            fontWeight: 800,
                            letterSpacing: "-0.055em",
                            color: colors.ink,
                        }}
                    >
                        Noch keine Beiträge vorhanden.
                    </Typography>

                    <Typography
                        sx={{
                            mb: 3,
                            maxWidth: 620,
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 16,
                            lineHeight: 1.7,
                            color: colors.muted,
                        }}
                    >
                        Starte mit dem ersten Beitrag für die Website. Du kannst ihn
                        zunächst als Entwurf speichern und später veröffentlichen.
                    </Typography>

                    <Button
                        type="button"
                        onClick={() => navigate("/admin/news/new")}
                        variant="contained"
                        sx={{
                            minHeight: 52,
                            px: 3.2,
                            borderRadius: 999,
                            backgroundColor: colors.brownDark,
                            color: "#fff",
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontWeight: 900,
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            boxShadow: "0 14px 30px rgba(111,81,54,0.22)",

                            "&:hover": {
                                backgroundColor: colors.brownDarker,
                                boxShadow: "0 18px 38px rgba(111,81,54,0.28)",
                            },
                        }}
                    >
                        Ersten Beitrag erstellen
                    </Button>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {news.map((item) => (
                        <Paper
                            key={item.id}
                            elevation={0}
                            sx={{
                                overflow: "hidden",
                                borderRadius: 5,
                                backgroundColor: colors.paper,
                                border: `1px solid ${colors.border}`,
                                boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
                            }}
                        >
                            <Box
                                sx={{
                                    p: {
                                        xs: 2.5,
                                        md: 3.2,
                                    },
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "1fr auto",
                                    },
                                    gap: {
                                        xs: 2.5,
                                        md: 3,
                                    },
                                    alignItems: "center",
                                }}
                            >
                                <Box>
                                    <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        gap={1}
                                        sx={{ mb: 1.4 }}
                                    >
                                        <Chip
                                            size="small"
                                            label={
                                                item.published
                                                    ? "Veröffentlicht"
                                                    : "Entwurf"
                                            }
                                            sx={{
                                                height: 28,
                                                borderRadius: 999,
                                                fontFamily: '"Nunito Sans", sans-serif',
                                                fontWeight: 900,
                                                color: item.published
                                                    ? colors.success
                                                    : colors.brownDark,
                                                backgroundColor: item.published
                                                    ? colors.successBg
                                                    : colors.draftBg,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={formatDate(item.created_at)}
                                            sx={{
                                                height: 28,
                                                borderRadius: 999,
                                                fontFamily: '"Nunito Sans", sans-serif',
                                                fontWeight: 900,
                                                color: colors.muted,
                                                backgroundColor: colors.white,
                                            }}
                                        />
                                    </Stack>

                                    <Typography
                                        component="h2"
                                        sx={{
                                            mb: 0.9,
                                            fontFamily: '"Quicksand", sans-serif',
                                            fontSize: {
                                                xs: "1.55rem",
                                                md: "1.95rem",
                                            },
                                            lineHeight: 1.08,
                                            fontWeight: 800,
                                            letterSpacing: "-0.045em",
                                            color: colors.ink,
                                        }}
                                    >
                                        {item.title || "Unbenannter Beitrag"}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            maxWidth: 760,
                                            fontFamily: '"Nunito Sans", sans-serif',
                                            fontSize: 15.5,
                                            lineHeight: 1.65,
                                            color: colors.muted,
                                        }}
                                    >
                                        {item.excerpt ||
                                            "Keine Kurzbeschreibung vorhanden."}
                                    </Typography>
                                </Box>

                                <Stack
                                    direction={{
                                        xs: "column",
                                        sm: "row",
                                        md: "column",
                                    }}
                                    spacing={1}
                                    sx={{
                                        minWidth: {
                                            md: 150,
                                        },
                                    }}
                                >
                                    <Button
                                        type="button"
                                        onClick={() =>
                                            navigate(`/admin/news/${item.id}`)
                                        }
                                        variant="outlined"
                                        sx={{
                                            minHeight: 44,
                                            borderRadius: 999,
                                            borderWidth: 2,
                                            borderColor: colors.brown,
                                            color: colors.brownDark,
                                            fontFamily: '"Nunito Sans", sans-serif',
                                            fontWeight: 900,
                                            textTransform: "none",

                                            "&:hover": {
                                                borderWidth: 2,
                                                borderColor: colors.brownDark,
                                                backgroundColor:
                                                    "rgba(155,118,83,0.08)",
                                            },
                                        }}
                                    >
                                        Bearbeiten
                                    </Button>

                                    <Button
                                        type="button"
                                        onClick={() => handleDelete(item.id)}
                                        variant="text"
                                        sx={{
                                            minHeight: 44,
                                            borderRadius: 999,
                                            color: colors.danger,
                                            fontFamily: '"Nunito Sans", sans-serif',
                                            fontWeight: 900,
                                            textTransform: "none",

                                            "&:hover": {
                                                backgroundColor: colors.dangerBg,
                                            },
                                        }}
                                    >
                                        Löschen
                                    </Button>
                                </Stack>
                            </Box>

                            <Divider
                                sx={{
                                    borderColor: "rgba(42,38,33,0.08)",
                                }}
                            />

                            <Box
                                sx={{
                                    px: {
                                        xs: 2.5,
                                        md: 3.2,
                                    },
                                    py: 1.5,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    backgroundColor: "rgba(255,255,255,0.36)",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 13,
                                        fontWeight: 800,
                                        color: colors.softMuted,
                                    }}
                                >
                                    ID: {item.id}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 13,
                                        fontWeight: 800,
                                        color: colors.softMuted,
                                    }}
                                >
                                    Erstellt am {formatDate(item.created_at)}
                                </Typography>
                            </Box>
                        </Paper>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

export default AdminNewsList;